import { useQuery } from '@apollo/client';
import {
  Hubspot,
  Marketo,
  Salesforce,
  SegmentCompany as Segment,
} from '@clearkit/icons';
import { Subscribe } from 'unstated';

import ActiveIntegrationsList from '~/components/ActiveIntegrationsList';
import IntegrationPickerCard from '~/components/IntegrationPickerCard';
import {
  IntegrationPickerLayout,
  IntegrationPickerRow,
} from '~/components/layouts/IntegrationPickerLayout';
import SidebarDrawer from '~/components/SidebarDrawer';
import UpgradeAccountForm from '~/components/UpgradeAccountForm';
import UserContainer from '~/containers/User';
import { AccountFlagEnum } from '~/generated/graphql';
import useFeatureFlag from '~/hooks/account/useFeatureFlag';
import Tile from '~/images/Tiles/Enrichment.svg';

import query from './query.graphql';

const LegacyEnrichmentList = () => {
  const { data: { account } = {}, loading } = useQuery(query);
  const [showUpgradeForm, setShowUpgradeForm] = useState(false);

  const hasHubspotFeatureFlag = useFeatureFlag('x_data_enrichment_hubspot');
  const hasMarketoFeatureFlag = useFeatureFlag('x_marketo_on_platform_beta');
  const hasDataHealthFlag = useFeatureFlag('x_enrichment_health');

  const allowPlatformHubspotConfig = useFeatureFlag(
    AccountFlagEnum.AllowPlatformHubspotConfig,
  ).enabled;
  const allowPlatformMarketoConfig = useFeatureFlag(
    AccountFlagEnum.AllowPlatformMarketoConfig,
  ).enabled;
  const allowPlatformSegmentConfig = useFeatureFlag(
    AccountFlagEnum.AllowPlatformSegmentConfig,
  ).enabled;
  const allowPlatformSalesforceConfig = useFeatureFlag(
    AccountFlagEnum.AllowPlatformSalesforceConfig,
  ).enabled;

  const hubspotUrl = hasHubspotFeatureFlag.enabled
    ? '/enrichment/hubspot'
    : 'hubspot.clearbit.com';
  const hubspotVariant = hasHubspotFeatureFlag.enabled
    ? IntegrationPickerCard.Variants.default
    : IntegrationPickerCard.Variants.external;

  const marketoUrl = hasMarketoFeatureFlag.enabled
    ? '/enrichment/marketo'
    : 'marketo.clearbit.com';

  const marketoVariant = hasMarketoFeatureFlag.enabled
    ? IntegrationPickerCard.Variants.default
    : IntegrationPickerCard.Variants.external;

  const hasPlanAccess = (integrationPlan) =>
    account?.plans.some((plan) => plan === integrationPlan);
  const integrations = [
    {
      Icon: Salesforce,
      type: 'salesforce',
      bgClass: 'bg-salesforce',
      title: 'Salesforce',
      text:
        'Enrich any account, contact, or lead, or your full database of records, within Salesforce.',
      tag: 'Accelerate Revenue',
      url: '/enrichment/salesforce',
      plan: 'enrichment_salesforce',
      hasAccess:
        hasPlanAccess('enrichment_salesforce') || allowPlatformSalesforceConfig,
    },
    {
      Icon: Marketo,
      type: 'marketo',
      bgClass: 'bg-marketo',
      title: 'Marketo',
      text:
        'Enrich any account, contact, or lead, or your full database of records, within Marketo.',
      tag: 'Accelerate Revenue',
      url: marketoUrl,
      plan: 'enrichment_marketo',
      variant: marketoVariant,
      hasAccess:
        hasPlanAccess('enrichment_marketo') || allowPlatformMarketoConfig,
    },
    {
      Icon: Segment,
      type: 'segment',
      bgClass: 'bg-segment',
      title: 'Segment',
      text: 'Enrich people and companies in Segment with Clearbit data.',
      tag: 'Convert Intent',
      url: 'segment.clearbit.com',
      plan: 'enrichment',
      variant: IntegrationPickerCard.Variants.external,
      hasAccess: hasPlanAccess('enrichment') || allowPlatformSegmentConfig,
    },
    {
      Icon: Hubspot,
      type: 'hubspot',
      bgClass: 'bg-hubspot',
      title: 'Hubspot',
      text:
        'Enrich and refresh new contacts, form submissions, or your entire contact database within HubSpot.',
      tag: 'Accelerate Revenue',
      url: hubspotUrl,
      plan: 'enrichment_hubspot',
      variant: hubspotVariant,
      hasAccess:
        hasPlanAccess('enrichment_hubspot') || allowPlatformHubspotConfig,
    },
  ];

  const accesibleIntegrations = [];
  const unaccesibleIntegrations = [];

  integrations.forEach((integration) => {
    if (integration.hasAccess) {
      accesibleIntegrations.push(integration);
    } else {
      unaccesibleIntegrations.push(integration);
    }
  });

  return (
    <Subscribe to={[UserContainer]}>
      {(userContainer) => {
        return (
          <IntegrationPickerLayout
            background="white"
            loading={loading}
            subtitle="Get rich firmographic data into the systems that matter most to your business"
            tile={<Tile />}
            title="Enrichment"
          >
            <div className="flex flex-col flex-grow">
              {hasDataHealthFlag.enabled && <ActiveIntegrationsList />}
              {accesibleIntegrations.length > 0 && (
                <IntegrationPickerRow
                  subtitle="Select your desired integration to get started."
                  title="Your integrations"
                >
                  {accesibleIntegrations.map((integration) => {
                    const objectType = 'enrichment';
                    const objectAction = 'update';
                    const objectSubtype = integration.type;

                    const hasPermission = userContainer.hasPermissionTo({
                      objectAction,
                      objectType,
                      objectSubtype,
                    });

                    return (
                      <IntegrationPickerCard
                        container={userContainer}
                        key={integration.title}
                        {...integration}
                        hasAccess
                        hasPermission={hasPermission}
                      />
                    );
                  })}
                </IntegrationPickerRow>
              )}
              {unaccesibleIntegrations.length > 0 && (
                <IntegrationPickerRow
                  secondary
                  subtitle="Select your desired integration to get started."
                  title="Available integrations"
                >
                  {unaccesibleIntegrations.map((integration) => {
                    return (
                      <IntegrationPickerCard
                        key={integration.title}
                        {...integration}
                        hasAccess={integration.hasAccess}
                        setShowUpgradeForm={setShowUpgradeForm}
                      />
                    );
                  })}
                </IntegrationPickerRow>
              )}
            </div>

            <SidebarDrawer
              onClose={() => {
                setShowUpgradeForm(false);
              }}
              open={showUpgradeForm}
            >
              <UpgradeAccountForm
                onClose={() => {
                  setShowUpgradeForm(false);
                }}
                product="dataEnrichment"
              />
            </SidebarDrawer>
          </IntegrationPickerLayout>
        );
      }}
    </Subscribe>
  );
};

export default LegacyEnrichmentList;
